import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import { RootLayout } from '@/app/layout/root-layout';
import { LoadingFallback } from '@/components/loading-fallback';
import { HomePage } from '@/pages/home';

const lazyPage = <T extends Record<string, React.ComponentType>>(
  loader: () => Promise<T>,
  pickKey: keyof T,
): React.LazyExoticComponent<React.ComponentType> =>
  lazy(async () => {
    const mod = await loader();
    return { default: mod[pickKey] };
  });

const BaumpflegeOverview = lazyPage(() => import('@/pages/baumpflege'), 'BaumpflegeOverview');
const KronenpflegePage = lazyPage(
  () => import('@/pages/baumpflege/kronenpflege'),
  'KronenpflegePage',
);
const TotholzentfernungPage = lazyPage(
  () => import('@/pages/baumpflege/totholzentfernung'),
  'TotholzentfernungPage',
);
const RueckschnittPage = lazyPage(
  () => import('@/pages/baumpflege/rueckschnitt'),
  'RueckschnittPage',
);
const FassadenschnittPage = lazyPage(
  () => import('@/pages/baumpflege/fassadenschnitt'),
  'FassadenschnittPage',
);
const LichtraumprofilschnittPage = lazyPage(
  () => import('@/pages/baumpflege/lichtraumprofilschnitt'),
  'LichtraumprofilschnittPage',
);
const KroneneinkuerzungPage = lazyPage(
  () => import('@/pages/baumpflege/kroneneinkuerzung'),
  'KroneneinkuerzungPage',
);
const JungbaumpflegePage = lazyPage(
  () => import('@/pages/baumpflege/jungbaumpflege'),
  'JungbaumpflegePage',
);
const KronensicherungPage = lazyPage(
  () => import('@/pages/baumpflege/kronensicherung'),
  'KronensicherungPage',
);
const BaumkontrollePage = lazyPage(
  () => import('@/pages/baumpflege/baumkontrolle'),
  'BaumkontrollePage',
);
const SturmschaedenPage = lazyPage(
  () => import('@/pages/baumpflege/sturmschaeden'),
  'SturmschaedenPage',
);

const BaumhausbauOverview = lazyPage(() => import('@/pages/baumhausbau'), 'BaumhausbauOverview');
const IndividuellerBaumhausbauPage = lazyPage(
  () => import('@/pages/baumhausbau/individueller-baumhausbau'),
  'IndividuellerBaumhausbauPage',
);
const PlanungBeratungPage = lazyPage(
  () => import('@/pages/baumhausbau/planung-beratung'),
  'PlanungBeratungPage',
);
const BaumschonendeBefestigungPage = lazyPage(
  () => import('@/pages/baumhausbau/baumschonende-befestigung'),
  'BaumschonendeBefestigungPage',
);
const SicherheitskonzeptePage = lazyPage(
  () => import('@/pages/baumhausbau/sicherheitskonzepte'),
  'SicherheitskonzeptePage',
);
const MaterialwahlPage = lazyPage(
  () => import('@/pages/baumhausbau/materialwahl'),
  'MaterialwahlPage',
);
const BaubegleitungPage = lazyPage(
  () => import('@/pages/baumhausbau/baubegleitung'),
  'BaubegleitungPage',
);
const FamilienprojektePage = lazyPage(
  () => import('@/pages/baumhausbau/familienprojekte'),
  'FamilienprojektePage',
);

const BaumfaellungOverview = lazyPage(() => import('@/pages/baumfaellung'), 'BaumfaellungOverview');
const BaumfaellungPage = lazyPage(
  () => import('@/pages/baumfaellung/baumfaellung'),
  'BaumfaellungPage',
);
const GefahrenfaellungPage = lazyPage(
  () => import('@/pages/baumfaellung/gefahrenfaellung'),
  'GefahrenfaellungPage',
);
const SeilklettertechnikPage = lazyPage(
  () => import('@/pages/baumfaellung/seilklettertechnik'),
  'SeilklettertechnikPage',
);
const StueckweisesAbtragenPage = lazyPage(
  () => import('@/pages/baumfaellung/stueckweises-abtragen'),
  'StueckweisesAbtragenPage',
);
const EntsorgungPage = lazyPage(() => import('@/pages/baumfaellung/entsorgung'), 'EntsorgungPage');
const StubbenfraesenPage = lazyPage(
  () => import('@/pages/baumfaellung/stubbenfraesen'),
  'StubbenfraesenPage',
);
const WurzelentfernungPage = lazyPage(
  () => import('@/pages/baumfaellung/wurzelentfernung'),
  'WurzelentfernungPage',
);
const NotfaellungPage = lazyPage(
  () => import('@/pages/baumfaellung/notfaellung'),
  'NotfaellungPage',
);
const GenehmigungenPage = lazyPage(
  () => import('@/pages/baumfaellung/genehmigungen'),
  'GenehmigungenPage',
);

const ReferenzenPage = lazyPage(() => import('@/pages/referenzen'), 'ReferenzenPage');
const UeberUnsPage = lazyPage(() => import('@/pages/ueber-uns'), 'UeberUnsPage');
const KontaktPage = lazyPage(() => import('@/pages/kontakt'), 'KontaktPage');
const EinsatzgebietPage = lazyPage(() => import('@/pages/einsatzgebiet'), 'EinsatzgebietPage');
const ImpressumPage = lazyPage(() => import('@/pages/impressum'), 'ImpressumPage');
const DatenschutzPage = lazyPage(() => import('@/pages/datenschutz'), 'DatenschutzPage');
const NotFoundPage = lazyPage(() => import('@/pages/not-found'), 'NotFoundPage');

const wrap = (Element: React.ComponentType): React.ReactNode => (
  <Suspense fallback={<LoadingFallback />}>
    <Element />
  </Suspense>
);

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/baumpflege', element: wrap(BaumpflegeOverview) },
      { path: '/baumpflege/kronenpflege', element: wrap(KronenpflegePage) },
      { path: '/baumpflege/totholzentfernung', element: wrap(TotholzentfernungPage) },
      { path: '/baumpflege/rueckschnitt', element: wrap(RueckschnittPage) },
      { path: '/baumpflege/fassadenschnitt', element: wrap(FassadenschnittPage) },
      { path: '/baumpflege/lichtraumprofilschnitt', element: wrap(LichtraumprofilschnittPage) },
      { path: '/baumpflege/kroneneinkuerzung', element: wrap(KroneneinkuerzungPage) },
      { path: '/baumpflege/jungbaumpflege', element: wrap(JungbaumpflegePage) },
      { path: '/baumpflege/kronensicherung', element: wrap(KronensicherungPage) },
      { path: '/baumpflege/baumkontrolle', element: wrap(BaumkontrollePage) },
      { path: '/baumpflege/sturmschaeden', element: wrap(SturmschaedenPage) },
      { path: '/baumhausbau', element: wrap(BaumhausbauOverview) },
      {
        path: '/baumhausbau/individueller-baumhausbau',
        element: wrap(IndividuellerBaumhausbauPage),
      },
      { path: '/baumhausbau/planung-beratung', element: wrap(PlanungBeratungPage) },
      {
        path: '/baumhausbau/baumschonende-befestigung',
        element: wrap(BaumschonendeBefestigungPage),
      },
      { path: '/baumhausbau/sicherheitskonzepte', element: wrap(SicherheitskonzeptePage) },
      { path: '/baumhausbau/materialwahl', element: wrap(MaterialwahlPage) },
      { path: '/baumhausbau/baubegleitung', element: wrap(BaubegleitungPage) },
      { path: '/baumhausbau/familienprojekte', element: wrap(FamilienprojektePage) },
      { path: '/baumfaellung', element: wrap(BaumfaellungOverview) },
      { path: '/baumfaellung/baumfaellung', element: wrap(BaumfaellungPage) },
      { path: '/baumfaellung/gefahrenfaellung', element: wrap(GefahrenfaellungPage) },
      { path: '/baumfaellung/seilklettertechnik', element: wrap(SeilklettertechnikPage) },
      { path: '/baumfaellung/stueckweises-abtragen', element: wrap(StueckweisesAbtragenPage) },
      { path: '/baumfaellung/entsorgung', element: wrap(EntsorgungPage) },
      { path: '/baumfaellung/stubbenfraesen', element: wrap(StubbenfraesenPage) },
      { path: '/baumfaellung/wurzelentfernung', element: wrap(WurzelentfernungPage) },
      { path: '/baumfaellung/notfaellung', element: wrap(NotfaellungPage) },
      { path: '/baumfaellung/genehmigungen', element: wrap(GenehmigungenPage) },
      { path: '/referenzen', element: wrap(ReferenzenPage) },
      { path: '/ueber-uns', element: wrap(UeberUnsPage) },
      { path: '/kontakt', element: wrap(KontaktPage) },
      { path: '/einsatzgebiet', element: wrap(EinsatzgebietPage) },
      { path: '/impressum', element: wrap(ImpressumPage) },
      { path: '/datenschutz', element: wrap(DatenschutzPage) },
      { path: '*', element: wrap(NotFoundPage) },
    ],
  },
]);
